import Button from '@shared/components/core/button/CoreButton';
import { StickyFooter } from '@shared/components/core/structure/CommonBodyLayoutStyles';
import CommandPickerDialog from '@shared/components/input/field/CommandPickerDialog';
import UnsavedChangesDialog from '@shared/components/loading/UnsavedChangesDialog';
import DeprecatedHotkeyDialog from '@shared/components/profile/DeprecatedHotkeyDialog';
import { CommandType } from '@shared/constants/CommandValues';
import { getRoute } from '@shared/hooks/getRoute';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import { useScrollToQueryParam } from '@shared/hooks/useScrollToQueryParam';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { MacroEditor } from '../macro/MacroEditor';

export default function ProfileEditPageContent() {
    const { t } = useTranslation();
    const { organizationId } = useParams();
    const location = useLocation();
    const selectRef = useScrollToQueryParam() ?? undefined;
    const editProfileContainer = EditProfileContainer.useContainer();
    const [macroToAddCommand, setMacroToAddCommand] = useState<MacroDisplayModel | undefined>();

    const handleSave = async () => {
        editProfileContainer.organizationIdRef.current = organizationId ?? null;
        const result = await editProfileContainer.saveChanges();
        return result;
    };

    const handleAddCommandWithType = (macro: MacroDisplayModel | undefined, commandType: CommandType) => {
        setMacroToAddCommand(undefined);
        editProfileContainer.setHasChanges(true);
        macro?.commands.push({ command: '', commandType });
        editProfileContainer.updateMacros([...editProfileContainer.macros]);
    };

    const saveAndExit = async () => {
        await handleSave();
    };

    if (!editProfileContainer.commandKeyMappings) return null;
    return (
        <>
            <MacroEditor
                selectRef={selectRef}
                disabled={editProfileContainer.isPreview}
                addMacroHref={getRoute(`${location.pathname}/controls/new`)}
            />
            <DeprecatedHotkeyDialog />
            <CommandPickerDialog
                open={!!macroToAddCommand}
                onClose={() => setMacroToAddCommand(undefined)}
                onCommandTypeSelected={(commandType: CommandType) =>
                    handleAddCommandWithType(macroToAddCommand, commandType)
                }
            />

            {editProfileContainer.hasChanges && (
                <UnsavedChangesDialog
                    hasChanges={editProfileContainer.hasChanges}
                    setHasChanges={editProfileContainer.setHasChanges}
                    onSave={async () => {
                        await editProfileContainer.saveChanges();
                    }}
                />
            )}

            <StickyFooter
                contentPosition="center"
                $visuallyHide={
                    !editProfileContainer.hasChanges ||
                    (editProfileContainer.id !== undefined &&
                        editProfileContainer.originatedByProfileId !== undefined &&
                        !editProfileContainer.hasChanges)
                }>
                <Button $color="primary" $size="large" startIcon="save" onClick={saveAndExit}>
                    {t('common:saveAndFinish')}
                </Button>
            </StickyFooter>
        </>
    );
}
